import {
  getModulePath,
  LEAGUE_MODULE_NAMES,
  overwriteRoutePaths,
} from "@leagueplatform/core";
import { RouteObject } from "react-router-dom";
import {
  CareFinderRoutes,
  AppointmentSchedulingRoutes,
  HealthJourneyRoutes,
  HealthProfileRoutes,
  AboutMeRoutes,
  CareCollectionsRoutes,
  CareDocumentsRoutes,
  AchievementsRoutes,
  DashboardRoutes,
  LabResultsRoutes,
} from "components/lazy-routes.component";
import { AuthViewLogin, AuthViewLogout } from "pages/auth/auth.pages";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { PreLoginLandingPage } from "pages/pre-login-landing/pre-login-landing.page";
import { PageNotFound } from "pages/page-not-found.page";
import { HomePage } from "pages/home/home.page";
import { ScreeningsPage } from "pages/screenings/screenings.page";
import { ScheduleAppointmentPage } from "pages/screenings/schedule.page";
import { AuthSessionContainer } from "components/auth-session-container/auth-session-container";
import {
  LegacyAboutMeRedirect,
  LEGACY_ABOUT_ME_PATH,
} from "components/legacy-about-me-redirect/legacy-about-me-redirect.component";
import { SchedulingFlowLauncher } from "components/scheduling-flow-launcher/scheduling-flow-launcher.component";
import { PersonalInformation } from "pages/personal-information/personal-information.page";
import { CommunicationPreferences } from "pages/communication-preferences/communication-preferences.page";
import { FAQ } from "pages/faq/faq.page";
import { AboutPage } from "pages/about/about.page";
import { ContactUs } from "pages/contact-us/contact-us.page";
import { PrivacyPolicy } from "pages/privacy-policy/privacy-policy.page";
import { TOS } from "pages/tos/tos.page";
import { MasonryPlaygroundPage } from "pages/masonry-playground/masonry-playground.page";
import { HQSDetailsPage } from "pages/hqs-details/hqs-details.page";
import { PhysicalResultsDownloadFormPage } from "pages/physical-results-download-form/physical-results-download-form.page";
import { PhysicalResultsUploadFormPage } from "pages/physical-results-upload-form/physical-results-upload-form.page";
import { PrivateRoutesContainer } from "components/private-routes-container/private-routes-container.component";
import { AccountSetupPage } from "pages/account-setup/account-setup.page";
import {
  HealthProfileRedirect,
  LEGACY_HEALTH_PROFILE_PATH,
} from "components/health-profile-redirect/health-profile-redirect.component";
import { ResourcesPage } from "pages/resources/resources.page";
import { ResourceDetailPage } from "pages/resources/resource-detail.page";
import { WhyScreeningsImportantPage } from "pages/learn/why-screenings-important.page";
import { HowToShareResultsPage } from "pages/learn/how-to-share-results.page";
import { ExplorePage } from "pages/explore/explore.page";
import { CareLibraryTopicPage } from "pages/care-library/care-library-topic.page";
import { CareLibrarySearchPage } from "pages/care-library/care-library-search.page";
import { AddedByMePage } from "pages/added-by-me/added-by-me.page";
import { HistoryPage } from "pages/history/history.page";
import { SelfCollectionSelectionPage } from "pages/self-collection/selection.page";
import { SelfCollectionAddressConfirmationPage } from "pages/self-collection/address-confirmation.page";
import { SelfCollectionOrderPlacedPage } from "pages/self-collection/order-placed.page";
import { SelfCollectionCancellationPage } from "pages/self-collection/cancellation.page";
import { SelfCollectionOrderCancelledPage } from "pages/self-collection/order-cancelled.page";
import { SelfCollectionInstructionsPage } from "pages/self-collection/instructions.page";
import { SelfCollectionCompleteActivationPage } from "pages/self-collection/complete-activation.page";
import { SelfCollectionActivationFormPage } from "pages/self-collection/activation-form.page";
import { SelfCollectionActivationCompletePage } from "pages/self-collection/activation-complete.page";

const { VITE_APP_ENV } = import.meta.env;

// Masonry Playground is only available in local and staging environments
const isDevelopmentOrStaging =
  VITE_APP_ENV === "staging" ||
  VITE_APP_ENV === "development" ||
  import.meta.env.DEV;

// Define League route config override
const leagueRoutesOverride = {
  [LEAGUE_MODULE_NAMES.onboarding]: "/onboarding",
  [LEAGUE_MODULE_NAMES.aboutMe]: "/my-profile",
  [LEAGUE_MODULE_NAMES.healthProfile]: "/health-profile",
  [LEAGUE_MODULE_NAMES.healthJourneyHistory]: "/member/health-history",
  // Masonry's "Related collections" cards deeplink to this literal path
  // (QUEST-2858) — matching it here avoids needing a legacy-path redirect.
  [LEAGUE_MODULE_NAMES.careCollections]: "/member/care-collections",
};

overwriteRoutePaths(leagueRoutesOverride);

const healthJourneyPaths = [
  `${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/*`,
  `${getModulePath(LEAGUE_MODULE_NAMES.healthProgress)}/*`,
  `${getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory)}/*`,
  `${getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}/*`,
];

export const appRoutes: RouteObject[] = [
  {
    path: `${APP_MANAGED_ROUTES.SIGN_IN}/*`,
    element: <AuthViewLogin />,
  },
  {
    path: `${APP_MANAGED_ROUTES.SIGN_OUT}/*`,
    element: <AuthViewLogout />,
  },
  {
    path: APP_MANAGED_ROUTES.LANDING_PAGE,
    element: <PreLoginLandingPage />,
  },
  {
    element: <AuthSessionContainer />,
    children: [
      {
        path: APP_MANAGED_ROUTES.ACCOUNT_SETUP,
        element: <AccountSetupPage />,
      },
      {
        element: <PrivateRoutesContainer />,
        children: [
          {
            path: APP_MANAGED_ROUTES.HOME,
            element: <HomePage />,
          },
          {
            path: APP_MANAGED_ROUTES.SCREENINGS,
            element: <ScreeningsPage />,
          },
          {
            path: APP_MANAGED_ROUTES.SCREENINGS_SCHEDULE,
            element: <ScheduleAppointmentPage />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.labResults)}/*`,
            element: <LabResultsRoutes />,
          },
          {
            path: `${APP_MANAGED_ROUTES.HISTORY}/*`,
            element: <HistoryPage />,
          },
          ...healthJourneyPaths.map((path) => ({
            path,
            element: <HealthJourneyRoutes />,
          })),
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.healthProfile)}/*`,
            element: <HealthProfileRoutes />,
          },
          {
            path: APP_MANAGED_ROUTES.RESOURCES,
            element: <ResourcesPage />,
          },
          {
            path: APP_MANAGED_ROUTES.RESOURCE_DETAIL,
            element: <ResourceDetailPage />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.aboutMe)}/*`,
            element: <AboutMeRoutes />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.careCollections)}/*`,
            element: <CareCollectionsRoutes />,
          },
          // Masonry nodes and the SDK deeplink into About Me by its legacy
          // path, which `processUrl` cannot rewrite once the URL carries a
          // query string — so Quest has to resolve it itself (QUEST-2873).
          {
            path: `${LEGACY_ABOUT_ME_PATH}/*`,
            element: <LegacyAboutMeRedirect />,
          },
          {
            path: `${LEGACY_HEALTH_PROFILE_PATH}/*`,
            element: <HealthProfileRedirect />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.careDocuments)}/*`,
            element: (
              <div className="care-documents-page">
                <CareDocumentsRoutes />
              </div>
            ),
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.rewardsAchievements)}/*`,
            element: <AchievementsRoutes />,
          },
          {
            path: `${getModulePath(
              LEAGUE_MODULE_NAMES.healthProfile
            )}/insights/*`,
            element: <DashboardRoutes />,
          },
          {
            path: APP_MANAGED_ROUTES.QUOTIENT_SCORE_DETAILS,
            element: <HQSDetailsPage />,
          },
          {
            path: APP_MANAGED_ROUTES.PHYSICAL_RESULTS_DOWNLOAD_FORM,
            element: <PhysicalResultsDownloadFormPage />,
          },
          {
            path: APP_MANAGED_ROUTES.PHYSICAL_RESULTS_UPLOAD_FORM,
            element: <PhysicalResultsUploadFormPage />,
          },
          {
            path: APP_MANAGED_ROUTES.LEARN_WHY_SCREENINGS_IMPORTANT,
            element: <WhyScreeningsImportantPage />,
          },
          {
            path: APP_MANAGED_ROUTES.LEARN_HOW_TO_SHARE_RESULTS,
            element: <HowToShareResultsPage />,
          },
          {
            path: APP_MANAGED_ROUTES.EXPLORE,
            element: <ExplorePage />,
          },
          {
            path: APP_MANAGED_ROUTES.CARE_LIBRARY_SEARCH,
            element: <CareLibrarySearchPage />,
          },
          {
            path: APP_MANAGED_ROUTES.CARE_LIBRARY_TOPIC,
            element: <CareLibraryTopicPage />,
          },
          {
            path: APP_MANAGED_ROUTES.ADDED_BY_ME_CAMPAIGNS,
            element: <AddedByMePage />,
          },
          {
            path: APP_MANAGED_ROUTES.SELF_COLLECTION_SELECT,
            element: <SelfCollectionSelectionPage />,
          },
          {
            path: APP_MANAGED_ROUTES.SELF_COLLECTION_ADDRESS_CONFIRMATION,
            element: <SelfCollectionAddressConfirmationPage />,
          },
          {
            path: APP_MANAGED_ROUTES.SELF_COLLECTION_ORDER_PLACED,
            element: <SelfCollectionOrderPlacedPage />,
          },
          {
            path: APP_MANAGED_ROUTES.SELF_COLLECTION_CANCELLATION,
            element: <SelfCollectionCancellationPage />,
          },
          {
            path: APP_MANAGED_ROUTES.SELF_COLLECTION_ORDER_CANCELLED,
            element: <SelfCollectionOrderCancelledPage />,
          },
          {
            path: APP_MANAGED_ROUTES.SELF_COLLECTION_INSTRUCTIONS,
            element: <SelfCollectionInstructionsPage />,
          },
          {
            path: APP_MANAGED_ROUTES.SELF_COLLECTION_ACTIVATION,
            element: <SelfCollectionCompleteActivationPage />,
          },
          {
            path: APP_MANAGED_ROUTES.SELF_COLLECTION_ACTIVATION_FORM,
            element: <SelfCollectionActivationFormPage />,
          },
          {
            path: APP_MANAGED_ROUTES.SELF_COLLECTION_ACTIVATION_COMPLETE,
            element: <SelfCollectionActivationCompletePage />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.careFinder)}/*`,
            element: <CareFinderRoutes />,
          },
          // The Screenings "View available services" tile is a Contentful-owned
          // deeplink to the scheduling flow. The SDK only runs the flow as an
          // overlay — mounted as a route its close (X) button is a no-op — so
          // the deeplink opens the overlay on Screenings instead (QUEST-2866).
          {
            path: `${getModulePath(
              LEAGUE_MODULE_NAMES.appointmentScheduling
            )}/services`,
            element: <SchedulingFlowLauncher />,
          },
          {
            path: `${getModulePath(
              LEAGUE_MODULE_NAMES.appointmentScheduling
            )}/*`,
            element: <AppointmentSchedulingRoutes />,
          },
          {
            path: APP_MANAGED_ROUTES.PERSONAL_INFORMATION,
            element: <PersonalInformation />,
          },
          {
            path: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES,
            element: <CommunicationPreferences />,
          },
          {
            path: APP_MANAGED_ROUTES.FAQ,
            element: <FAQ />,
          },
          {
            path: APP_MANAGED_ROUTES.ABOUT,
            element: <AboutPage />,
          },
          {
            path: APP_MANAGED_ROUTES.CONTACT_US,
            element: <ContactUs />,
          },
          {
            path: APP_MANAGED_ROUTES.PRIVACY_POLICY,
            element: <PrivacyPolicy />,
          },
          {
            path: APP_MANAGED_ROUTES.TOS,
            element: <TOS />,
          },
          // Masonry Playground - only available in local/staging environments
          ...(isDevelopmentOrStaging
            ? [
                {
                  path: APP_MANAGED_ROUTES.MASONRY_PLAYGROUND,
                  element: <MasonryPlaygroundPage />,
                },
              ]
            : []),
          {
            path: "/*",
            element: <PageNotFound />,
          },
          {
            path: "/masonry/:version/:id/*",
            lazy: async () => ({
              Component: (await import("@leagueplatform/masonry"))
                .GenericMasonryPage,
            }),
          },
        ],
      },
    ],
  },
];
